"use client";

import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Close, CreditScoreRounded } from "@mui/icons-material";
import Image from "next/image";
import { useProductPageContext } from "@/app/(main)/products/[...slugs]/Providers/useProductPageContext";
import { useQuery } from "@tanstack/react-query";
import { useGqlClient } from "@/Providers/GQLProvider";
import { usePaymentSource } from "@/app/(main)/_components/PaymentSourceProvider";
import { CartPricingMode } from "@/__generated__/sdk";
import { effectiveSellerPrice, getVariantSellerOffers } from "../sellerOffers";
const roundToHundred = (value: number): number => {
  return Math.round(value / 100) * 100;
};
export default function InstallmentPayBanner() {
  const gqlClient = useGqlClient();
  const { activeGateway } = usePaymentSource();
  const { data } = useQuery({
    queryKey: [gqlClient.getPaymentGatewaysOfProduct.name, activeGateway],
    queryFn: () => gqlClient.getPaymentGatewaysOfProduct({ source: activeGateway }),
    staleTime: 50_000,
  });

  const [open, setOpen] = useState(true);

  const context = useProductPageContext();

  if (!context || (context.variants && context.variants.length <= 0)) return null;

  const { variantToShow } = context;
  if (!variantToShow || !variantToShow.hasStock || !open) return null;

  const gateways = (data?.paymentGateways ?? []).filter((gateway) => gateway.installment);

  

  if (!gateways.length) return null;

  const creditOffer = getVariantSellerOffers(variantToShow).find((offer) => offer.mode === CartPricingMode.Credit);
  if (!creditOffer) return null;

  const installmentPrice = roundToHundred(effectiveSellerPrice(creditOffer) / 4).toLocaleString("fa-IR");


  return (
    <Box
      component="section"
      aria-label="اطلاعات پرداخت اقساطی"
      data-snappay-banner
      sx={{
        width: "100%",
        borderRadius: 3,
        px: { xs: 1, sm: 1.75 },
        py: { xs: 1, sm: 1.5 },
        background: "linear-gradient(135deg, #eef8ff 0%, #e0f2ff 55%, #f5fbff 100%)",
        border: "1px solid rgba(0, 142, 250, 0.14)",
        boxShadow: "0 8px 24px rgba(0, 142, 250, 0.08)",
        overflow: "hidden",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1} alignItems="flex-start" flex={1} minWidth={0}>
            <Box
              aria-hidden="true"
              sx={{
                width: { xs: 38, sm: 44 },
                height: { xs: 38, sm: 44 },
                minWidth: { xs: 38, sm: 44 },
                borderRadius: "14px",
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 142, 250, 0.10)",
              }}
            >
              <CreditScoreRounded sx={{ color: "#008efa", fontSize: { xs: 20, sm: 24 } }} />
            </Box>

            <Box flex={1} minWidth={0}>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 800,
                  color: "#1167a8",
                  lineHeight: 1.5,
                }}
              >
                این محصول رو می‌تونی قسطی هم بخری 💚
              </Typography>

              <Typography
                component="p"
                sx={{
                  mt: 0.4,
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 500,
                  color: "#3f7fb2",
                  lineHeight: 1.9,
                  wordBreak: "break-word",
                }}
              >
                پرداخت در <Box component="strong" sx={{ fontWeight: 800 }}>{`۴ قسط`}</Box> {`بدون کارمزد`}، هر قسط{" "}
                <Box component="strong" sx={{ fontWeight: 800, color: "#0077d6" }}>
                  {installmentPrice} تومان
                </Box>
              </Typography>
            </Box>
          </Stack>

          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{
              color: "#6ea6cf",
              mt: -0.5,
              mr: -0.5,
              flexShrink: 0,
            }}
            aria-label="بستن کادر پرداخت اقساطی"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Stack
          component="ul"
          direction="row"
          useFlexGap
          flexWrap="wrap"
          gap={0.75}
          sx={{
            listStyle: "none",
            p: 0,
            m: 0,
            pl: { xs: 0, sm: 0.25 },
          }}
          aria-label="درگاه‌های پرداخت اقساطی پشتیبانی‌شده"
        >
          {gateways.map((gateway) => (
            <Stack
              key={gateway.name}
              component="li"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                maxWidth: "100%",
                px: 0.5,
                py: 0.5,
                backgroundColor: "rgba(255,255,255,0.85)",
                borderRadius: 3,
                border: "1px solid rgba(17, 103, 168, 0.08)",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: { xs: 26, sm: 32 },
                  height: { xs: 26, sm: 32 },
                  flexShrink: 0,
                  borderRadius: 2.5,
                  overflow: "hidden",
                  bgcolor: "#d8efff",
                  color: "#1167a8",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {gateway.logo?.address ? (
                  <Image
                    src={gateway.logo.address}
                    alt={`لوگوی ${gateway.name}`}
                    fill
                    sizes="(max-width: 600px) 26px, 32px"
                    loading="lazy"
                    fetchPriority="low"
                    style={{ objectFit: "contain" }}
                  />
                ) : (
                  gateway.name.slice(0, 1)
                )}
              </Box>

              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 700,
                  color: "#1167a8",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {gateway.name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
